/**
 * 目标骰次曲线、收入膨胀 B、按价反推 B、估算骰次——服务端 src/game/target-rolls.ts / income-growth.ts 的 bigint 镜像。
 *
 * 管端以前直接 import 服务端那两个文件，它们按 Number 算并在 INT32 / 1e12 处抛错，
 * 关卡加到几百关时整列显示「—」。经济数值没有上限（leo 2026-10-16），这里全用 bigint 精确算：
 *   曲线    base × (bps/10000)^k 一次除法四舍五入，至少 1
 *   E / ρ   页面状态是小数（3.1 / 0.9），按服务端存法折成 eTargetMilliB / costRatioBps 两个整数再算
 */
import { divRound, toBig } from "./big.ts";

/** E（每骰产出锚，小数）→ 服务端存的千分整数 */
export const eMilliOf = (E: number) => (Number.isFinite(E) ? BigInt(Math.round(E * 1000)) : 0n);
/** ρ（消耗系数，小数）→ 服务端存的万分整数。没配（NaN）按 0，调用方据此判「算不出」 */
export const rhoBpsOf = (rho: number) => (Number.isFinite(rho) ? BigInt(Math.round(rho * 10000)) : 0n);
/** E × ρ 的分母：千分 × 万分 */
export const ENV_SCALE = 10_000_000n;

const pos = (v: unknown): bigint | null => {
  const b = toBig(v);
  return b != null && b > 0n ? b : null;
};

/** base × (bps/10000)^k，四舍五入、至少 1 */
export function growth(base: bigint, bps: bigint, k: number): bigint {
  const n = BigInt(k);
  if (bps === 10_000n && n >= 0n) return base < 1n ? 1n : base;
  const v = divRound(base * bps ** n, 10_000n ** n);
  return v < 1n ? 1n : v;
}

/**
 * 目标骰次：曲线关闭（base 为 0 / 空）读原逐关值；开着时单关覆盖优先，否则 base × g^(关号−1)。
 * 增长系数 0 视为 1（不增长）。参数不合法（负数、关号不是正整数）给 null。
 */
export function targetRollsAt(legacy: unknown, level: number, base: unknown, growthBps: unknown, override?: unknown): bigint | null {
  const b = toBig(base);
  if (!b) return toBig(legacy);
  const g = toBig(growthBps) ?? 0n;
  if (b < 0n || g < 0n || !Number.isSafeInteger(level) || level < 1) return null;
  if (override != null && override !== "") return pos(override);
  return growth(b, g || 10_000n, level - 1);
}

/**
 * 收入膨胀后的 B：bps 为 0 / 空沿用原 B；否则 (首关覆盖 || 锚) × (bps/10000)^step。
 * 锚不是正数、参数为负给 null。
 */
export function incomeGrowthAt(legacyB: bigint, anchorB: bigint | null, step: number, bps: unknown, anchorOverride?: unknown): bigint | null {
  const k = toBig(bps) ?? 0n;
  if (k === 0n) return legacyB;
  const over = toBig(anchorOverride) ?? 0n;
  if (k < 0n || over < 0n || !Number.isSafeInteger(step) || step < 0 || !(anchorB != null && anchorB > 0n)) return null;
  return growth(over || anchorB, k, step);
}

/** 从价格反推 B：total ÷ (rolls × E × ρ)，四舍五入、至少 1。任一项不是正数给 null */
export function bFromPrice(total: bigint | null, rolls: bigint | null, E: number, rho: number): bigint | null {
  const e = eMilliOf(E), r = rhoBpsOf(rho);
  if (total == null || rolls == null || !(total > 0n) || !(rolls > 0n) || !(e > 0n) || !(r > 0n)) return null;
  const v = divRound(total * ENV_SCALE, rolls * e * r);
  return v < 1n ? 1n : v;
}

/** 估算骰次：total ÷ (B × E × ρ) 向上取整、至少 1。不是对玩家实际骰次的保证 */
export function estimateRollsBig(total: bigint | null, incomeB: bigint | null, E: number, rho: number): bigint | null {
  const e = eMilliOf(E), r = rhoBpsOf(rho);
  if (total == null || incomeB == null || !(total > 0n) || !(incomeB > 0n) || !(e > 0n) || !(r > 0n)) return null;
  const n = total * ENV_SCALE, d = incomeB * e * r;
  const v = (n + d - 1n) / d;
  return v < 1n ? 1n : v;
}

/**
 * 单掷金币版本的估算骰次：造价 ÷ (本关 g × ρ)，向上取整，至少 1。
 * g 是千分整数（gMilli），与 model.ts algoC 的 model 2 分支同一套刻度（骰次 × gMilli × ρ万分 ÷ ENV_SCALE）
 */
export function estimateRollsG(total: bigint | null, gMilli: bigint | null, rho: number): bigint | null {
  const r = rhoBpsOf(rho);
  if (total == null || gMilli == null || !(total > 0n) || !(gMilli > 0n) || !(r > 0n)) return null;
  const n = total * ENV_SCALE, d = gMilli * r;
  const v = (n + d - 1n) / d;
  return v < 1n ? 1n : v;
}

/** 骰次 × B × E × ρ（未取整的分子，分母 ENV_SCALE） */
export const envProduct = (rolls: bigint, b: bigint, E: number, rho: number) => rolls * b * eMilliOf(E) * rhoBpsOf(rho);
